/** Helpers for the additive /v2 per-entity endpoints - URL building, error
 * classification, and the actual request/retry functions, kept free of react-query/React
 * context (only a type-only import from "axios", plus the also framework-free "@/api/errors")
 * so they're unit-testable against a fake AxiosInstance without mocking React.
 * api/entityOpsV2.ts wires these into hooks. */
import { type AxiosInstance, isAxiosError } from "axios";
import { isProjectLockedError, ProjectLockedError } from "@/api/errors";

export type V2EntityKind = "character" | "location" | "shot";

export interface V2EntityItem<TData> {
  readonly id: string;
  readonly version: number;
  readonly order?: number;
  readonly data: TData;
}

export interface V2MetaItem<TData> {
  readonly version: number;
  readonly data: TData;
}

export interface V2ReorderResult {
  readonly id: string;
  readonly version: number;
  readonly order: number;
}

export function buildEntityV2Path(projectId: string, kind: V2EntityKind, entityId: string): string {
  return `/v2/${encodeURIComponent(projectId)}/${kind}/${encodeURIComponent(entityId)}`;
}

export function buildProjectMetaV2Path(projectId: string): string {
  return `/v2/${encodeURIComponent(projectId)}/meta`;
}

/** Thrown when a v2 write's expected_version is stale - the caller should refetch
 * the entity (for its fresh version) before retrying, not blindly resend the same body. */
export class EntityConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EntityConflictError";
  }
}

export interface V2ErrorLike {
  readonly status?: number;
  readonly errorMessage?: string;
}

export function classifyEntityV2Error(errorLike: V2ErrorLike, fallback: string): Error {
  if (errorLike.status === 409) {
    return new EntityConflictError(errorLike.errorMessage ?? "This item was changed by someone else - reload and try again.");
  }

  return new Error(errorLike.errorMessage ?? fallback);
}

const errorLikeFromAxios = (error: unknown): V2ErrorLike => {
  if (!isAxiosError<{ error?: string }>(error)) {
    return {};
  }

  return { status: error.response?.status, errorMessage: error.response?.data?.error };
};

/**
 * Classify a raw axios error from a /v2 write call: 423 (project locked by its owner) takes
 * priority over the generic 409/other mapping in classifyEntityV2Error so callers (the global
 * mutation onError in index.tsx) can react to ProjectLockedError specifically.
 */
function classifyV2ErrorFromAxios(error: unknown, fallback: string): Error {
  if (isProjectLockedError(error)) {
    const errorLike = errorLikeFromAxios(error);
    return new ProjectLockedError(errorLike.errorMessage ?? "This project is locked by its owner.");
  }

  return classifyEntityV2Error(errorLikeFromAxios(error), fallback);
}

/** warn, not error: a resolved conflict is the retry mechanism working as intended. */
async function retryAfterConflict<T>(
  operation: string,
  subject: string,
  expectedVersion: number,
  freshVersion: number,
  retry: () => Promise<T>
): Promise<T> {
  console.warn(`[entityOpsV2] ${operation} conflict on ${subject} (had v${expectedVersion}, server has v${freshVersion}) - refetching and retrying.`);
  try {
    return await retry();
  } catch (retryError) {
    console.error(`[entityOpsV2] Retry after ${operation} conflict on ${subject} also failed - giving up.`);
    throw retryError;
  }
}

export async function fetchEntityV2<TData>(
  axiosClient: AxiosInstance,
  projectId: string,
  kind: V2EntityKind,
  entityId: string
): Promise<V2EntityItem<TData> | null> {
  try {
    const { data } = await axiosClient.get<V2EntityItem<TData>>(buildEntityV2Path(projectId, kind, entityId));
    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw classifyEntityV2Error(errorLikeFromAxios(error), "Something went wrong when loading the entity.");
  }
}

interface SaveEntityV2RawVars<TData> {
  readonly projectId: string;
  readonly entityId: string;
  readonly data: TData;
  readonly expectedVersion: number;
}

async function saveEntityV2Raw<TData>(
  axiosClient: AxiosInstance,
  kind: V2EntityKind,
  vars: SaveEntityV2RawVars<TData>
): Promise<V2EntityItem<TData>> {
  try {
    const response = await axiosClient.post<V2EntityItem<TData>>(buildEntityV2Path(vars.projectId, kind, vars.entityId), {
      data: vars.data,
      expected_version: vars.expectedVersion
    });
    return response.data;
  } catch (error) {
    throw classifyV2ErrorFromAxios(error, "Something went wrong when saving the entity.");
  }
}

export interface SaveEntityV2Vars<TData> {
  readonly projectId: string;
  readonly entityId: string;
  readonly currentData: TData;
  readonly applyChange: (current: TData) => TData;
  readonly expectedVersion: number;
}

export async function saveEntityV2WithRetry<TData>(
  axiosClient: AxiosInstance,
  kind: V2EntityKind,
  vars: SaveEntityV2Vars<TData>
): Promise<V2EntityItem<TData>> {
  try {
    return await saveEntityV2Raw(axiosClient, kind, {
      projectId: vars.projectId,
      entityId: vars.entityId,
      data: vars.applyChange(vars.currentData),
      expectedVersion: vars.expectedVersion
    });
  } catch (error) {
    if (!(error instanceof EntityConflictError)) {
      throw error;
    }

    const fresh = await fetchEntityV2<TData>(axiosClient, vars.projectId, kind, vars.entityId);
    if (fresh === null) {
      throw error;
    }

    return await retryAfterConflict("save", `${kind}/${vars.entityId}`, vars.expectedVersion, fresh.version, () =>
      saveEntityV2Raw(axiosClient, kind, {
        projectId: vars.projectId,
        entityId: vars.entityId,
        data: vars.applyChange(fresh.data),
        expectedVersion: fresh.version
      })
    );
  }
}

export interface DeleteEntityV2Vars {
  readonly projectId: string;
  readonly kind: V2EntityKind;
  readonly entityId: string;
  readonly expectedVersion: number;
}

async function deleteEntityV2Raw(axiosClient: AxiosInstance, vars: DeleteEntityV2Vars): Promise<void> {
  try {
    await axiosClient.delete(buildEntityV2Path(vars.projectId, vars.kind, vars.entityId), {
      params: { expected_version: vars.expectedVersion }
    });
  } catch (error) {
    throw classifyV2ErrorFromAxios(error, "Something went wrong when deleting the entity.");
  }
}

export async function deleteEntityV2WithRetry(axiosClient: AxiosInstance, vars: DeleteEntityV2Vars): Promise<void> {
  try {
    await deleteEntityV2Raw(axiosClient, vars);
  } catch (error) {
    if (!(error instanceof EntityConflictError)) {
      throw error;
    }

    const fresh = await fetchEntityV2(axiosClient, vars.projectId, vars.kind, vars.entityId);
    if (fresh === null) {
      return;
    }

    await retryAfterConflict("delete", `${vars.kind}/${vars.entityId}`, vars.expectedVersion, fresh.version, () =>
      deleteEntityV2Raw(axiosClient, { ...vars, expectedVersion: fresh.version })
    );
  }
}

/**
 * Exactly one of `order` (a raw fractional value the caller already has, e.g. from a
 * prior v2 GET of two neighboring siblings) or `position` (a 0-based target array
 * index) must be set - `position` is the common case, since the whole-project shape
 * never exposes any entity's actual `order` value to the frontend.
 */
export type ReorderEntityV2Vars = {
  readonly projectId: string;
  readonly kind: V2EntityKind;
  readonly entityId: string;
  readonly expectedVersion: number;
} & ({ readonly order: number; readonly position?: undefined } | { readonly position: number; readonly order?: undefined });

async function reorderEntityV2Raw(axiosClient: AxiosInstance, vars: ReorderEntityV2Vars): Promise<V2ReorderResult> {
  try {
    const { data } = await axiosClient.patch<V2ReorderResult>(buildEntityV2Path(vars.projectId, vars.kind, vars.entityId), {
      ...(vars.order === undefined ? { position: vars.position } : { order: vars.order }),
      expected_version: vars.expectedVersion
    });
    return data;
  } catch (error) {
    throw classifyV2ErrorFromAxios(error, "Something went wrong when reordering the entity.");
  }
}

export async function reorderEntityV2WithRetry(axiosClient: AxiosInstance, vars: ReorderEntityV2Vars): Promise<V2ReorderResult> {
  try {
    return await reorderEntityV2Raw(axiosClient, vars);
  } catch (error) {
    if (!(error instanceof EntityConflictError)) {
      throw error;
    }

    const fresh = await fetchEntityV2(axiosClient, vars.projectId, vars.kind, vars.entityId);
    if (fresh === null) {
      throw error;
    }

    return await retryAfterConflict("reorder", `${vars.kind}/${vars.entityId}`, vars.expectedVersion, fresh.version, () =>
      reorderEntityV2Raw(axiosClient, { ...vars, expectedVersion: fresh.version } as ReorderEntityV2Vars)
    );
  }
}

export async function fetchProjectMetaV2Request<TData>(axiosClient: AxiosInstance, projectId: string): Promise<V2MetaItem<TData> | null> {
  try {
    const { data } = await axiosClient.get<V2MetaItem<TData>>(buildProjectMetaV2Path(projectId));
    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw classifyEntityV2Error(errorLikeFromAxios(error), "Something went wrong when loading project meta.");
  }
}

export async function updateProjectMetaV2Request<TData>(
  axiosClient: AxiosInstance,
  projectId: string,
  partialData: Partial<TData>,
  expectedVersion: number
): Promise<V2MetaItem<TData>> {
  try {
    const { data } = await axiosClient.patch<V2MetaItem<TData>>(buildProjectMetaV2Path(projectId), {
      data: partialData,
      expected_version: expectedVersion
    });
    return data;
  } catch (error) {
    throw classifyV2ErrorFromAxios(error, "Something went wrong when updating project meta.");
  }
}

/** update_meta merges partialData onto whatever is currently stored (re-read
 * server-side on every call), so unlike saveEntityV2WithRetry a conflict retry only
 * needs a fresh version number, not a reapplied `applyChange` against fresh data. */
export async function updateProjectMetaV2WithRetry<TData>(
  axiosClient: AxiosInstance,
  projectId: string,
  partialData: Partial<TData>,
  expectedVersion: number
): Promise<V2MetaItem<TData>> {
  try {
    return await updateProjectMetaV2Request<TData>(axiosClient, projectId, partialData, expectedVersion);
  } catch (error) {
    if (!(error instanceof EntityConflictError)) {
      throw error;
    }

    const fresh = await fetchProjectMetaV2Request<TData>(axiosClient, projectId);
    if (fresh === null) {
      throw error;
    }

    return await retryAfterConflict("meta update", `project ${projectId}`, expectedVersion, fresh.version, () =>
      updateProjectMetaV2Request<TData>(axiosClient, projectId, partialData, fresh.version)
    );
  }
}
